import { useMemo, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, MoreHorizontal, Trash2 } from "lucide-react";
import yn from "yn";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUserPermission from "~/hooks/useUserPermission";
import { cn } from "~/lib/utils";
import { ROUTE_404, ROUTE_NEXTGEN_DATASETS } from "~/router/constants";
import { apiMutate } from "~/utils/apiClient";
import { AccessLevelBadge } from "./AccessLevelBadge";

const { VITE_CATALOG_API } = import.meta.env;

const BASE_NAV_ITEMS = [
  { label: "Overview", path: "overview" },
  { label: "Dashboards", path: "dashboards" },
  { label: "Tables", path: "tables" },
  { label: "Files", path: "files" },
] as const;

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
  cn(
    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-accent text-accent-foreground"
      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
  );

export const DatasetDetail = () => {
  const navigate = useNavigate();
  const { data: dataset, error, isLoading, mutate } = useDataset();
  const canEdit = useDatasetPermission("edit:catalog-dataset");
  const canDelete = useDatasetPermission("delete:catalog-dataset") && canEdit;
  const hasCohortExplorerPermission = useUserPermission("view:cohort-explorer-config-app");
  const { showSuccess, showError } = useToast();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  const cohortExplorerEnabled =
    yn(import.meta.env.VITE_COHORT_EXPLORER_ENABLED, { default: false }) &&
    hasCohortExplorerPermission;
  const userAccessEnabled = yn(import.meta.env.VITE_DATASET_ACCESS_ENABLED, { default: false });

  const headerBreadcrumbs = useMemo(
    () => [
      { label: "All Datasets", href: ROUTE_NEXTGEN_DATASETS },
      ...(dataset ? [{ label: dataset.title }] : []),
    ],
    [dataset]
  );

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs });

  const handleDelete = async () => {
    if (!dataset) return;
    setIsDeleting(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        is_deleted: true,
      });
      showSuccess(`"${dataset.title}" deleted`);
      navigate(ROUTE_NEXTGEN_DATASETS, { replace: true });
    } catch {
      showError("Failed to delete dataset");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
    }
  };

  const handleCopy = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    showSuccess(`${label} copied`);
  };

  const handleRename = async () => {
    if (!dataset) return;
    const trimmed = renameTitle.trim();
    if (!trimmed || trimmed === dataset.title) {
      setRenameOpen(false);
      return;
    }
    setIsRenaming(true);
    try {
      await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        title: trimmed,
      });
      mutate();
      showSuccess("Dataset renamed");
      setRenameOpen(false);
    } catch {
      showError("Failed to rename dataset");
    } finally {
      setIsRenaming(false);
    }
  };

  if (error && (error as { status?: number }).status === 404) {
    return <Navigate to={ROUTE_404} replace />;
  }

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading dataset" className="flex min-h-full">
        <div className="w-56 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="mx-1 my-1 h-8 rounded-md" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load dataset</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <nav
          aria-label="Dataset sections"
          className="flex w-56 shrink-0 flex-col border-r border-border"
        >
          <div className="flex flex-col gap-3 border-b border-border px-4 py-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
                <p className="w-full break-words text-sm font-medium text-foreground">
                  {dataset.title}
                </p>
                <AccessLevelBadge accessLevel={dataset.access_level} />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 shrink-0 text-muted-foreground"
                    aria-label={`Actions for ${dataset.title}`}
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
                  {canEdit && (
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameTitle(dataset.title);
                        setRenameOpen(true);
                      }}
                    >
                      Rename…
                    </DropdownMenuItem>
                  )}
                  {canEdit && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={() => handleCopy(dataset.id, "ID")}>
                    Copy ID
                  </DropdownMenuItem>
                  {dataset.slug && (
                    <DropdownMenuItem onClick={() => handleCopy(dataset.slug!, "Slug")}>
                      Copy slug
                    </DropdownMenuItem>
                  )}
                  {canDelete && <DropdownMenuSeparator />}
                  {canDelete && (
                    <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
                      <Trash2 />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex flex-col gap-1 px-4 py-3">
            {BASE_NAV_ITEMS.map(({ label, path }) => (
              <NavLink key={path} to={path} end className={navLinkClassName}>
                {label}
              </NavLink>
            ))}
            {dataset.aiAgentsEnabled && (
              <NavLink to="ai-context" className={navLinkClassName}>
                AI Context
              </NavLink>
            )}
            {dataset.aiAgentsEnabled && (
              <NavLink to="ai-skills" className={navLinkClassName}>
                AI Skills
              </NavLink>
            )}
            {cohortExplorerEnabled && (
              <NavLink to="cohort-explorer" className={navLinkClassName}>
                Cohort Explorer
              </NavLink>
            )}
            {userAccessEnabled && (
              <NavLink to="user-access" className={navLinkClassName}>
                User Access
              </NavLink>
            )}
          </div>
        </nav>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={dataset} />
        </div>
      </div>

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => {
          if (!open) setRenameOpen(false);
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Rename Dataset</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rename-dataset">Name</Label>
            <Input
              id="rename-dataset"
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleRename()}
              disabled={isRenaming}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)} disabled={isRenaming}>
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={isRenaming || !renameTitle.trim()}>
              {isRenaming ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{dataset.title}&rdquo;</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this dataset?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
